import type { LucideIcon } from 'lucide-react';
import {
  ClipboardList,
  FileSearch,
  MessageSquare,
  FilePenLine,
  FileText,
  FolderSearch,
  Scale,
} from 'lucide-react';

/** Harvey/Lexis-Modi — Mapping auf bestehende ARKO-Intents (n8n unverändert). */
export type ArkoHubModeId =
  | 'vertragspruefung'
  | 'mrg_mietrecht'
  | 'mandatsannahme'
  | 'fragen'
  | 'verfassen'
  | 'zusammenfassen'
  | 'analysieren';

/**
 * Intent am Hub-Mode und an Composer-Tool-Pins.
 * Speichern = MEMCORE-Pin (kein Harvey-Kachel) — Pin-Chrome nutzt denselben Slot.
 */
export type ArkoHubModeIntent =
  | 'rechtsfrage'
  | 'umformulieren'
  | 'zusammenfassen'
  | 'erklaeren'
  | 'speichern';

export interface ArkoHubMode {
  id: ArkoHubModeId;
  label: string;
  description: string;
  icon: LucideIcon;
  /** InlineChat-QuickAction-Intent oder Navigation */
  intent?: ArkoHubModeIntent;
  /** Prompt-Prefix für Sofortstart im Chat */
  promptPrefix?: string;
  accent: string;
  /** Phase 1 — Aktenwächter-Anbindung (Konfig, UI folgt in 1.2) */
  matterType?: string;
  akteTemplateId?: string;
  guidedTile?: boolean;
}

/** Top-3 geführte Workflows (Phase 1, Andreas Go 14.07.2026). */
export const ARKO_GUIDED_TILES: ArkoHubMode[] = [
  {
    id: 'vertragspruefung',
    label: 'Vertragsprüfung',
    description: 'Geführte Klauselprüfung mit Aktenbezug und Checkliste.',
    icon: FileSearch,
    intent: 'erklaeren',
    promptPrefix:
      'Geführte Vertragsprüfung (Dienstleistungsvertrag/NDA):\n' +
      'Bitte prüfe die wesentlichen Klauseln (Haftung, Laufzeit, Kündigung, Gerichtsstand) und gib eine strukturierte Risikoeinschätzung.\n\n',
    accent: 'violet',
    matterType: 'vertrag_beratung',
    akteTemplateId: 'vertrag-beratung',
    guidedTile: true,
  },
  {
    id: 'mrg_mietrecht',
    label: 'MRG-Mietrecht',
    description: 'Schnellcheck MRG-Voll-/Teilanwendung, Pflichtschritte und belegte Normen.',
    icon: Scale,
    intent: 'rechtsfrage',
    promptPrefix:
      'MRG-Mietrecht Schnellcheck (Österreich):\n' +
      'Bitte prüfe Voll-/Teilanwendung des MRG, Pflichtschritte (Mahnung, Aufkündigung) und nenne relevante Normen mit Beleg.\n\n',
    accent: 'emerald',
    matterType: 'mrg_mietrecht',
    guidedTile: true,
  },
  {
    id: 'mandatsannahme',
    label: 'Mandatsannahme',
    description: 'Erstberatung vorbereiten: Kontakt, Anliegen, Konfliktcheck und Aktenanlage.',
    icon: ClipboardList,
    intent: 'erklaeren',
    promptPrefix:
      'Mandatsannahme / Erstberatung vorbereiten:\n' +
      'Bitte führe mich durch die Aufnahme (Mandant, Anliegen, Konfliktcheck) und nenne fehlende Pflichtpunkte.\n\n',
    accent: 'blue',
    matterType: 'vertrag_beratung',
    akteTemplateId: 'vertrag-beratung',
    guidedTile: true,
  },
];

/** Legacy-Modi — weiterhin im Composer-Tray (Slice 1.3). */
export const ARKO_HUB_MODES_LEGACY: ArkoHubMode[] = [
  {
    id: 'fragen',
    label: 'Fragen',
    description: 'Rechtsfragen mit belegten Normen aus dem Bestand.',
    icon: MessageSquare,
    intent: 'rechtsfrage',
    promptPrefix: 'Rechtsfrage (belegte Normen):\n\n',
    accent: 'violet',
  },
  {
    id: 'verfassen',
    label: 'Verfassen',
    description: 'Memos, Schreiben und Klauseln professionell formulieren.',
    icon: FilePenLine,
    intent: 'umformulieren',
    promptPrefix: 'Bitte formuliere folgenden Text professionell um:\n\n',
    accent: 'violet',
  },
  {
    id: 'zusammenfassen',
    label: 'Zusammenfassen',
    description: 'Auf das Wesentliche reduzieren — Entscheidungen und Akten.',
    icon: FileText,
    intent: 'zusammenfassen',
    promptPrefix: 'Fasse folgenden Text präzise zusammen:\n\n',
    accent: 'violet',
  },
  {
    id: 'analysieren',
    label: 'Analysieren',
    description: 'Sachverhalt subsumieren — Tatbestandsmerkmale gegen belegte Normen.',
    icon: FolderSearch,
    intent: 'rechtsfrage',
    promptPrefix:
      'Analysiere folgenden Sachverhalt:\n\n',
    accent: 'violet',
  },
];

/** Home/Hub: Top-3 geführte Kacheln (Phase 1). */
export const ARKO_HUB_MODES: ArkoHubMode[] = ARKO_GUIDED_TILES;

/** Sichtbare Beschriftung nur aus der bestehenden Modus-Liste. Unbekannt und leer bleiben leer. */
export function arkoHubModeLabel(modeId?: string | null): string {
  const id = modeId?.trim() || '';
  if (!id) return '';
  return ARKO_HUB_MODES_LEGACY.find((mode) => mode.id === id)?.label ?? '';
}

/** null = kein Modus. undefined = unbekannte Id, nicht speichern. */
export function nextAkteHubModeId(picked?: string | null): string | null | undefined {
  const id = picked?.trim() || '';
  if (!id) return null;
  if (!arkoHubModeLabel(id)) return undefined;
  return id;
}

export const ARKO_INTENT_EVENT = 'arkorbit:arko-intent';
/** Phase 1.3 — startet geführten Kachel-Flow (nicht nur Prompt-Prefix). */
export const ARKO_GUIDED_FLOW_EVENT = 'arkorbit:arko-guided-flow';

type GuidedTileModeId = Extract<
  ArkoHubModeId,
  'vertragspruefung' | 'mrg_mietrecht' | 'mandatsannahme'
>;

/** Warteschlange wenn Kachel-Klick vor Chat-Mount (Home → Chat-Navigation). */
let pendingGuidedFlowId: GuidedTileModeId | null = null;

export function queueGuidedFlow(modeId: GuidedTileModeId): void {
  pendingGuidedFlowId = modeId;
}

export function consumePendingGuidedFlow(): GuidedTileModeId | null {
  const id = pendingGuidedFlowId;
  pendingGuidedFlowId = null;
  return id;
}

export interface ArkoHubIntentDetail {
  intent?: ArkoHubMode['intent'];
  promptPrefix?: string;
  modeId: ArkoHubModeId;
  matterType?: string;
  akteTemplateId?: string;
  guidedTile?: boolean;
}

export function dispatchArkoHubIntent(mode: ArkoHubMode): void {
  if (typeof window === 'undefined') return;
  const detail: ArkoHubIntentDetail = {
    intent: mode.intent,
    promptPrefix: mode.promptPrefix,
    modeId: mode.id,
    matterType: mode.matterType,
    akteTemplateId: mode.akteTemplateId,
    guidedTile: mode.guidedTile ?? false,
  };
  if (mode.guidedTile && isGuidedTileModeId(mode.id)) {
    queueGuidedFlow(mode.id);
    window.dispatchEvent(new CustomEvent(ARKO_GUIDED_FLOW_EVENT, { detail }));
    return;
  }
  window.dispatchEvent(new CustomEvent(ARKO_INTENT_EVENT, { detail }));
}

function isGuidedTileModeId(id: ArkoHubModeId): id is GuidedTileModeId {
  return id === 'vertragspruefung' || id === 'mrg_mietrecht' || id === 'mandatsannahme';
}
